/**
 * ReviewStep — read-only summary of the assembled draft. The "Submit"
 * button calls the submit mutation; the parent renders SubmitOutcomePanel.
 */
import type { JSX } from 'react';

import type { DraftPayload } from '../../lib/api/types';

import { formatMoney } from './formatMoney';

interface ReviewStepProps {
  payload: DraftPayload;
  onSubmit: () => void;
  onBack: () => void;
  isSubmitting: boolean;
  errorMessage?: string;
}

export function ReviewStep({
  payload,
  onSubmit,
  onBack,
  isSubmitting,
  errorMessage,
}: ReviewStepProps): JSX.Element {
  return (
    <section className="space-y-4" data-testid="review-step">
      <header>
        <h2 className="text-xl font-semibold text-slate-900">Review and submit</h2>
        <p className="text-sm text-slate-600">
          Verify everything before issuing the tracking number.
        </p>
      </header>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <PartyCard title="Sender" party={payload.sender} />
        <PartyCard title="Receiver" party={payload.receiver} />
      </div>

      <div className="rounded border border-slate-200 bg-white p-4">
        <h3 className="text-sm font-semibold text-slate-700">Package</h3>
        <dl className="mt-2 grid grid-cols-2 gap-2 text-sm">
          <Row label="Weight">{payload.package?.weightKg} kg</Row>
          <Row label="Service">{payload.serviceClass}</Row>
          <Row label="Declared value">
            {payload.package?.declaredValueNgn
              ? formatMoney(payload.package.declaredValueNgn)
              : '—'}
          </Row>
          <Row label="Quoted price">
            {payload.pricing?.amountNgn
              ? formatMoney(payload.pricing.amountNgn)
              : '—'}
          </Row>
          <Row label="Description" wide>
            {payload.package?.contentDescription ?? '—'}
          </Row>
        </dl>
      </div>

      {errorMessage !== undefined && (
        <div className="rounded border border-red-300 bg-red-50 p-3 text-sm text-red-800">
          {errorMessage}
        </div>
      )}

      <div className="flex justify-between">
        <button
          type="button"
          onClick={onBack}
          className="rounded border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700"
        >
          Back
        </button>
        <button
          type="button"
          onClick={onSubmit}
          disabled={isSubmitting}
          data-testid="review-submit"
          className="rounded bg-nipost-600 px-4 py-2 text-sm font-semibold text-white disabled:bg-slate-300"
        >
          {isSubmitting ? 'Submitting…' : 'Submit'}
        </button>
      </div>
    </section>
  );
}

interface PartyCardProps {
  title: string;
  party?: DraftPayload['sender'];
}

function PartyCard({ title, party }: PartyCardProps): JSX.Element {
  return (
    <div className="rounded border border-slate-200 bg-white p-4">
      <h3 className="text-sm font-semibold text-slate-700">{title}</h3>
      {party ? (
        <div className="mt-2 space-y-1 text-sm text-slate-700">
          <p className="font-medium">{party.name}</p>
          <p className="text-xs">{party.phone}</p>
          {party.email !== undefined && <p className="text-xs">{party.email}</p>}
          <p className="text-xs">
            {party.addressLine1}
            {party.addressLine2 !== undefined ? `, ${party.addressLine2}` : ''}
          </p>
          <p className="text-xs">
            {party.city}, {party.state} {party.postalCode ?? ''} ({party.countryCode})
          </p>
        </div>
      ) : (
        <p className="mt-2 text-sm text-slate-400">Not yet captured</p>
      )}
    </div>
  );
}

interface RowProps {
  label: string;
  children: React.ReactNode;
  wide?: boolean;
}

function Row({ label, children, wide }: RowProps): JSX.Element {
  return (
    <div className={wide === true ? 'col-span-2' : ''}>
      <dt className="text-xs uppercase tracking-wide text-slate-500">{label}</dt>
      <dd className="text-sm text-slate-900">{children}</dd>
    </div>
  );
}
